import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { nip19 } from 'nostr-tools';

import {
  GroundContext,
  type GroundContextType,
  type GroundIdentity,
  type GroundSettings,
  type GroundStats,
} from '@/contexts/GroundContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getGuestPubkey } from '@/lib/ground/identity';
import { ownsProtocol as protocolGrantsAccess } from '@/lib/ground/protocols';

const DEFAULT_SETTINGS: GroundSettings = {
  elevenLabsApiKey: '',
  elevenLabsVoiceId: '',
  nwcConnectionString: '',
  demoMode: true,
};

const DEFAULT_STATS: GroundStats = {
  sessions: 0,
  steps: 0,
  satsEarned: 0,
  zapsSent: 0,
  satsSent: 0,
};

export function GroundProvider({ children }: { children: ReactNode }) {
  const [rawSettings, setRawSettings] = useLocalStorage<Partial<GroundSettings>>(
    'ground:settings',
    {},
  );
  const [stats, setStats] = useLocalStorage<GroundStats>('ground:stats', DEFAULT_STATS);
  const [checkedItems, setCheckedItems] = useLocalStorage<string[]>('ground:roadmap', []);
  const [zapsByEvent, setZapsByEvent] = useLocalStorage<Record<string, number>>(
    'ground:zaps',
    {},
  );
  const [completedSections, setCompletedSections] = useLocalStorage<string[]>(
    'ground:foundations-sections',
    [],
  );
  const [completedDays, setCompletedDays] = useLocalStorage<number[]>(
    'ground:foundations-days',
    [],
  );
  const [journals, setJournals] = useLocalStorage<Record<string, string>>(
    'ground:foundations-journals',
    {},
  );
  const [purchases, setPurchases] = useLocalStorage<string[]>('ground:purchases', []);

  const { user } = useCurrentUser();

  const [guestPubkey] = useState(() => getGuestPubkey());

  const settings = useMemo<GroundSettings>(
    () => ({ ...DEFAULT_SETTINGS, ...rawSettings }),
    [rawSettings],
  );

  const identity = useMemo<GroundIdentity>(() => {
    if (user) {
      return { pubkey: user.pubkey, npub: nip19.npubEncode(user.pubkey), isGuest: false };
    }
    return { pubkey: guestPubkey, npub: nip19.npubEncode(guestPubkey), isGuest: true };
  }, [user, guestPubkey]);

  const updateSettings = useCallback(
    (patch: Partial<GroundSettings>) => {
      setRawSettings((current) => ({ ...current, ...patch }));
    },
    [setRawSettings],
  );

  const addSession = useCallback(() => {
    setStats((current) => ({ ...current, sessions: current.sessions + 1 }));
  }, [setStats]);

  const addZapSent = useCallback(
    (sats: number) => {
      setStats((current) => ({
        ...current,
        zapsSent: current.zapsSent + 1,
        satsSent: current.satsSent + sats,
      }));
    },
    [setStats],
  );

  const addSatsEarned = useCallback(
    (sats: number) => {
      setStats((current) => ({ ...current, satsEarned: current.satsEarned + sats }));
    },
    [setStats],
  );

  const resetStats = useCallback(() => {
    setStats(DEFAULT_STATS);
  }, [setStats]);

  const toggleItem = useCallback(
    (id: string) => {
      const isChecked = checkedItems.includes(id);
      setCheckedItems(
        isChecked ? checkedItems.filter((item) => item !== id) : [...checkedItems, id],
      );
      return !isChecked;
    },
    [checkedItems, setCheckedItems],
  );

  const resetRoadmap = useCallback(() => {
    setCheckedItems([]);
  }, [setCheckedItems]);

  const recordZap = useCallback(
    (eventId: string, sats: number) => {
      setZapsByEvent((current) => ({ ...current, [eventId]: (current[eventId] ?? 0) + sats }));
    },
    [setZapsByEvent],
  );

  // --- Foundations ---

  const completeSection = useCallback(
    (id: string) => {
      setCompletedSections((current) =>
        current.includes(id) ? current : [...current, id],
      );
    },
    [setCompletedSections],
  );

  const isDayComplete = useCallback(
    (day: number) => completedDays.includes(day),
    [completedDays],
  );

  const saveJournal = useCallback(
    (day: number, text: string) => {
      setJournals((current) => ({ ...current, [String(day)]: text }));
    },
    [setJournals],
  );

  const completeDay = useCallback(
    (day: number) => {
      setCompletedDays((current) => (current.includes(day) ? current : [...current, day]));
    },
    [setCompletedDays],
  );

  const resetFoundations = useCallback(() => {
    setCompletedSections([]);
    setCompletedDays([]);
    setJournals({});
  }, [setCompletedSections, setCompletedDays, setJournals]);

  const foundationsComplete = completedDays.includes(5);

  // --- Protocols ---

  const purchase = useCallback(
    (id: string) => {
      setPurchases((current) => (current.includes(id) ? current : [...current, id]));
    },
    [setPurchases],
  );

  const ownsProtocol = useCallback(
    (id: string) => protocolGrantsAccess(purchases, id),
    [purchases],
  );

  const resetPurchases = useCallback(() => {
    setPurchases([]);
  }, [setPurchases]);

  const value = useMemo<GroundContextType>(
    () => ({
      settings,
      updateSettings,
      stats: { ...stats, steps: checkedItems.length },
      addSession,
      addZapSent,
      addSatsEarned,
      resetStats,
      identity,
      checkedItems,
      toggleItem,
      resetRoadmap,
      zapsByEvent,
      recordZap,
      completedSections,
      completeSection,
      completedDays,
      isDayComplete,
      foundationsComplete,
      journals,
      saveJournal,
      completeDay,
      resetFoundations,
      purchases,
      purchase,
      ownsProtocol,
      resetPurchases,
    }),
    [
      settings,
      updateSettings,
      stats,
      checkedItems,
      addSession,
      addZapSent,
      addSatsEarned,
      resetStats,
      identity,
      toggleItem,
      resetRoadmap,
      zapsByEvent,
      recordZap,
      completedSections,
      completeSection,
      completedDays,
      isDayComplete,
      foundationsComplete,
      journals,
      saveJournal,
      completeDay,
      resetFoundations,
      purchases,
      purchase,
      ownsProtocol,
      resetPurchases,
    ],
  );

  return <GroundContext.Provider value={value}>{children}</GroundContext.Provider>;
}
